import GroupAddressNameGenerator from "../addressGeneration/GroupAddressNameGenerator";
import { dataUrlToCanvas, imageToCanvas, loadImageFromUrl } from "../modelViews/GraficElementUtils";
import { itx, loadLanguage } from "../translationItx/itx";
import { initialUiLanguage, normalizeUiLanguage } from "./uiLanguage";
import BaseModel from "./BaseModel";
import type { PdfExportConfig } from "../pdf/PdfExportConfig";
import { ApplicationMode, PrefixMode, enumAvailableLanguages } from "./SettingsEnums";
import type { EditMode } from "../../ConceptOne/src/types/C1_AppTypes";
import { C1_DOCUMENT_LOGO } from "../../ConceptOne/src/C1_Const";
import {
  ConnectionDashboardSettings,
  normalizeConnectionDashboardSelection,
  type ConnectionDashboardId,
} from "./ConnectionDashboardSettings";

const defaultDawingItemColors = {
  button: "#5C7DEB", // no itx
  consumer: "rgb(158, 211, 109)", // no itx
  light: "#29E99F", // no itx
  heating: "#F4B942", // no itx
  sensor: "#B18AD4", // no itx
  jalousie: "#A1BFDA", // no itx
  windowContact: "#5988B2", // no itx
  supply: "#EFBDEB", // no itx
  plug: "#C9A47C", // no itx
  plugWithoutCable: "#E4CFB7", // no itx
  lock: "#58C6DA", // no itx
};

export function drawingItemColors() {
  return defaultDawingItemColors;
}

export default class Settings extends BaseModel {
  public get className(): string {
    return "Settings"; // no itx
  }

  constructor() {
    super();
    // re-apply the persisted dark-mode choice as early as possible so the UI does not flash light on reload
    document.documentElement.classList.toggle("dark-mode", this.darkMode);
  }

  /** Identifies which shared application shell currently owns the UI. */
  applicationMode = ApplicationMode.Main;

  /** Remembers the main-app editing mode locally, across project changes and restarts. */
  get mainEditMode(): EditMode {
    const value = localStorage.getItem("mainEditMode");
    // Restore legacy names as the single electrical mode used by detail engineering.
    if (["electrical", "EloConcept", "EloDetailMaster", "interaction", "interactionConcept"].includes(value ?? ""))
      return "EloDetail";
    return value === "furnishing" || value === "EloDetail" ? value : "building";
  }

  set mainEditMode(value: EditMode) {
    const mode = value === "EloConcept" || value === "interactionConcept" ? "EloDetail" : value;
    if (mode === this.mainEditMode) return;
    localStorage.setItem("mainEditMode", mode);
    this.raiseOnChange("mainEditMode");
  }

  private _floorsHaveIndividualAxes = false;
  private _c1TagByIndex?: boolean;
  private _c1TagEditable?: boolean;
  get c1TagByIndex(): boolean {
    return this._c1TagByIndex ?? globalThis.state?.c1Project?.settings.data.tagByIndex ?? true;
  }
  set c1TagByIndex(value: boolean | undefined) {
    if (value === this._c1TagByIndex) return;
    this.beforeChange("c1TagByIndex");
    this._c1TagByIndex = value;
    this.changed("c1TagByIndex");
  }
  get c1TagEditable(): boolean {
    return this._c1TagEditable ?? globalThis.state?.c1Project?.settings.data.tagEditable ?? false;
  }
  set c1TagEditable(value: boolean | undefined) {
    if (value === this._c1TagEditable) return;
    this.beforeChange("c1TagEditable");
    this._c1TagEditable = value;
    this.changed("c1TagEditable");
  }

  /** Local PDF background preference shared by the main app and C1 editor. */
  get pdfBackgroundOpacity(): number {
    const stored = localStorage.getItem("pdfBackgroundOpacity");
    const value = stored === null ? 50 : Number(stored);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 50;
  }

  set pdfBackgroundOpacity(value: number) {
    const next = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 50;
    if (next === this.pdfBackgroundOpacity) return;
    localStorage.setItem("pdfBackgroundOpacity", String(next));
    this.raiseOnChange("pdfBackgroundOpacity");
  }

  /** Project setting: floor plans may have unrelated coordinate systems. */
  get floorsHaveIndividualAxes(): boolean {
    return this._floorsHaveIndividualAxes;
  }
  set floorsHaveIndividualAxes(value: boolean) {
    const next = value === true;
    if (next === this._floorsHaveIndividualAxes) return;
    this.beforeChange("floorsHaveIndividualAxes");
    this._floorsHaveIndividualAxes = next;
    this.changed("floorsHaveIndividualAxes");
  }

  private _masterPdfConfig?: PdfExportConfig;

  private _connectionDashboardVisibleTrees = normalizeConnectionDashboardSelection(undefined);
  readonly connectionDashboardSettings = new ConnectionDashboardSettings(() =>
    this.raiseOnUpdateUi("connectionDashboardSettings"),
  );
  private _connectionDashboardShowBlueWires = false;
  private _connectionDashboardShowPEWires = false;

  /** Transient visibility of blue conductors in ConnectionDashboard wire details. */
  get connectionDashboardShowBlueWires(): boolean {
    return this._connectionDashboardShowBlueWires;
  }
  set connectionDashboardShowBlueWires(value: boolean) {
    if (this._connectionDashboardShowBlueWires === value) return;
    this._connectionDashboardShowBlueWires = value;
    this.raiseOnUpdateUi("connectionDashboardShowBlueWires");
  }

  /** Transient visibility of PE conductors, independent of the blue-conductor setting. */
  get connectionDashboardShowPEWires(): boolean {
    return this._connectionDashboardShowPEWires;
  }
  set connectionDashboardShowPEWires(value: boolean) {
    if (this._connectionDashboardShowPEWires === value) return;
    this._connectionDashboardShowPEWires = value;
    this.raiseOnUpdateUi("connectionDashboardShowPEWires");
  }

  /** Returns a copy so changes always pass through the project's persistence notifications. */
  get connectionDashboardVisibleTrees(): ConnectionDashboardId[] {
    return [...this._connectionDashboardVisibleTrees];
  }

  set connectionDashboardVisibleTrees(value: readonly string[] | undefined) {
    const next = normalizeConnectionDashboardSelection(value);
    if (next.join(",") === this._connectionDashboardVisibleTrees.join(",")) return;
    this.beforeChange("connectionDashboardVisibleTrees");
    this._connectionDashboardVisibleTrees = next;
    this.changed("connectionDashboardVisibleTrees");
  }

  get masterPdfConfig(): PdfExportConfig | undefined {
    return this._masterPdfConfig ? structuredClone(this._masterPdfConfig) : undefined;
  }

  set masterPdfConfig(value: PdfExportConfig | undefined) {
    this.beforeChange("masterPdfConfig");
    this._masterPdfConfig = value ? structuredClone(value) : undefined;
    this.changed("masterPdfConfig");
  }

  groupAddressNameGenerator = new GroupAddressNameGenerator(this);

  private companyNameBack: string | undefined;

  // userShort: string = localStorage.getItem("user")
  //   ? JSON.parse(localStorage.getItem("user") as string).name
  //   : "";

  userName: string | undefined =
    localStorage.getItem("user") != "" ? JSON.parse(localStorage.getItem("user") as string)?.name : undefined;

  ///////////////////////////////////////////////////////////////////
  private _companyLogo: string = "";

  get companyLogo(): string {
    return this._companyLogo;
  }

  set companyLogo(value: string) {
    const newValue = value ?? "";
    if (this._companyLogo === newValue) return;
    this.beforeChange("companyLogo");
    this._companyLogo = newValue;
    // dont set companyLogoSize to 0 it will be calculated when the logo is loaded or changed, so that the default logo size is used until the real size is calculated
    this.companyLogoSize = {
      width: 1,
      height: 1,
    };
    this.changed("companyLogo");
  }

  companyLogoSize = {
    width: 1,
    height: 1,
  };

  _defaultCompanyLogo: string | undefined;
  private defaultCompanyLogoSource?: string;

  get defaultCompanyLogo(): string {
    return this.applicationMode === ApplicationMode.ConceptOne ? C1_DOCUMENT_LOGO : "/images/logo-detail-small-2.png";
  }

  async loadDefaultCompanyLogo() {
    const source = this.defaultCompanyLogo;
    if (!this._defaultCompanyLogo || this.defaultCompanyLogoSource !== source) {
      const img = await loadImageFromUrl(source);
      if (!img) return;

      const canvas = await imageToCanvas(img);
      if (!canvas) return;

      this._defaultCompanyLogo = canvas.toDataURL();
      this.defaultCompanyLogoSource = source;
      this.companyLogoSize = {
        width: img.width,
        height: img.height,
      };
    }
  }

  get companyLogoOrDefault(): string | undefined {
    const source = this.defaultCompanyLogo;
    const cachedLogo = this.defaultCompanyLogoSource === source ? this._defaultCompanyLogo : undefined;
    if (!cachedLogo) void this.loadDefaultCompanyLogo();
    const defaultLogo = cachedLogo || source;
    // only show company logo if the user is professional
    if (!state.projectLicense.checkIfProfessionalAbo()) {
      return defaultLogo;
    }
    return this.companyLogo || defaultLogo;
  }

  async calcCompanyLogoSize() {
    const image = await dataUrlToCanvas(this.companyLogoOrDefault);
    if (image) {
      this.companyLogoSize = {
        width: image.width,
        height: image.height,
      };
    }
  }

  companyColorBackground: string | undefined;
  companyColorText: string | undefined;

  /////////////////////////////////////////////////////////////////////////////////////

  // Room drawing

  keepCabinetAndScenesInsideView = true;

  colorTheme = "red"; // no itx
  showPageBorder = true;
  consumerScale = "0.9";
  consumerScaleFloorPlan = "0.7";

  getConsumerScale(forFloorPlan): number {
    if (forFloorPlan) {
      //
      //
      // in floorpänen geben wir generell alle symbole um mit einen verkleineung aus
      // ansonsten dient der Faktor zur anpassung an frühere Versionen
      //
      return parseFloat(this.consumerScaleFloorPlan) * this.getConsumerScale100Percent(forFloorPlan);
    } else {
      return parseFloat(this.consumerScale) * this.getConsumerScale100Percent(forFloorPlan);
    }
  }

  getConsumerScale100Percent(forFloorPlan): number {
    if (forFloorPlan) {
      return 0.6;
    } else {
      return 0.8;
    }
  }

  getFontScale(forFloorPlan): number {
    if (forFloorPlan) {
      return (parseFloat(this.consumerScaleFloorPlan) * 16) / 0.6;
    } else {
      return (parseFloat(this.consumerScale) * 16) / 0.8;
    }
  }

  consumerPrefix = {
    switch: "L", // no itx
    dimming: "L", // no itx
    rgb: "L", // no itx
    // rgbw: "L",
    tw: "L", // no itx
    staircaseLight: "L", // no itx
    blind: "R", // no itx
    jalousie: "J", // no itx
    heating: "H", // no itx
    // button: "T", // no itx
    outlet: "P", // same channeltype as e230->generate app only with channelType
    switchOutlet: "S", // no itx
    e230: "23E", // no itx
    e400: "40E", // no itx
    s12: "12V", // no itx
    s24: "24V", // no itx
    lan: "LAN", // no itx
    // must not start with "LAN", otherwise its tags would be counted into the plain LAN numbering pool (startsWith match)
    lan2: "DLAN", // no itx
    input: "I", // no itx
    intermediateDistribution: "V", // no itx
    memo: "M", // no itx
    // sensor: "PM", // no itx
  };
  devicePrefix = {
    actor: "A", // no itx
    button: "T", // no itx
    decentralActor: "DA", // no itx
    sensor: "PM", // no itx
    contact: "K", // no itx
    elcb: "Q", // no itx
    fuse: "F", // no itx
    terminalStrip: "X", // no itx
    terminal: "", // no itx
    multiplexer: "M", // no itx
  };
  cableTypeDefaultForConnectableObjs = {
    // Steckdosen: NYM-J 5x2,5 mm² (auch schaltbare)-
    //   Verbraucher 400V: NYM-J 5x2,5 mm² -
    //   Verbraucher 230V: NYM-J 5x1,5 mm² -
    //   Licht: NYM-J 5x1,5 mm² (Alles Typen Licht)-
    //   Rollläden/Jalousien: NYM-J 5x1,5 mm²-
    //   Kontakt: YCYM Y(St)Y2x2x0,8 (lila)
    //   Heizung: -
    Switch: "C5.15", // no itx
    Dimmer: "C5.15", // no itx
    Rgb: "Cy4.08", // no itx
    TuneableWhite: "C5.15", // no itx
    StaircaseLight: "C5.15", // no itx
    Blind: "C5.15", // no itx
    Jalousie: "C5.15", // no itx
    Heating: "C", // no itx
    Outlet: "C5.25", // no itx
    SwitchOutlet: "C5.25", // no itx
    PlugSwitchable: "C5.25", // no itx
    suply230V: "C5.15", // no itx
    suply400V: "C5.25", // no itx
    suply24V: "Cy4.08", // no itx
    suply12V: "Cy4.08", // no itx
    Input: "Cy4.08", // no itx^

    Button: "Cy4.08", // no itx
    Sensor: "Cy4.08", // no itx
    Lan: "CLan", // no itx
    Lan2: "CLan2", // no itx
  };

  fuseTagSchema: "A" | "B" | "C" = "A";
  includeElcbTagInFuseFullTag = false;

  // settigs for room plans
  // connectionWidth = "3";

  /// from now on automaticly via scale ..

  showIcons: boolean = true;
  showTags: boolean = true;

  get drawingItemColors() {
    return drawingItemColors();
  }

  //////////////////////////////////////////////////////////////////////////

  showConsumerTagOrNames = true;

  showConnectionTexts = false;

  showMenuButtonTooltips = true;
  private _useNavigatorBreakLines = true;
  get useNavigatorBreakLines(): boolean {
    return this._useNavigatorBreakLines;
  }
  set useNavigatorBreakLines(value: boolean) {
    if (this._useNavigatorBreakLines === value) return;
    this.beforeChange("useNavigatorBreakLines");
    this._useNavigatorBreakLines = value;
    this.changed("useNavigatorBreakLines");
  }
  openWithOneClick = false;

  showCabinetPlaning = true;

  showResultWithJson = true;

  showSmallHeader = true;

  customerView = false;
  // uppercase vor teg name
  tagNameAlwaysUpperCase = true;

  defaultBackgroundPictureElements = { fade: 80, scale: 1.0 };

  #language = initialUiLanguage();
  #languageChange: Promise<boolean> | undefined;

  get language(): enumAvailableLanguages {
    return this.#language;
  }
  set language(value: enumAvailableLanguages) {
    void this.setUiLanguage(value);
  }

  get uiLanguage(): enumAvailableLanguages {
    return this.language;
  }
  set uiLanguage(newValue: enumAvailableLanguages) {
    void this.setUiLanguage(newValue);
  }

  /** Sole language switch for both shells, including translations and UI invalidation. */
  async setUiLanguage(value: string): Promise<boolean> {
    const language = normalizeUiLanguage(value);
    // Settings snapshots must not change the live application's language.
    if (globalThis.state?.settings !== this) {
      this.#language = language;
      return true;
    }
    if (language === this.#language && this.#languageChange) return this.#languageChange;
    this.#language = language;
    localStorage.setItem("itx-language", language);
    this.raiseOnUpdateUi("language");
    const request = loadLanguage().then((loaded) => {
      if (loaded && globalThis.state?.settings === this && this.#language === language) {
        state.translateValueFunctions.translateNamesAfterLanguageSelection();
        this.raiseOnUpdateUi("language");
      }
      return loaded;
    });
    this.#languageChange = request;
    try {
      return await request;
    } finally {
      if (this.#languageChange === request) this.#languageChange = undefined;
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////
  get secondScreenSupport(): boolean {
    return localStorage.getItem("secondScreenSupport") == "true" || false;
  }

  set secondScreenSupport(newValue: boolean) {
    localStorage.setItem("secondScreenSupport", newValue ? "true" : "false"); // no itx
  }

  ///////////////////////////////////////////////////////////////////////////////////////

  get darkMode(): boolean {
    return localStorage.getItem("darkMode") == "true" || false;
  }

  set darkMode(newValue: boolean) {
    localStorage.setItem("darkMode", newValue ? "true" : "false"); // no itx
    document.documentElement.classList.toggle("dark-mode", newValue);
  }

  ///////////////////////////////////////////////////////////////////////////////////////

  get uiBackgroundColor(): string {
    if (!state.projectLicense.checkIfProfessionalAbo()) {
      return "var(--app-background)"; // no itx
    }
    let localStorageColor = localStorage.getItem("ui-background-color") || undefined;
    if (!localStorageColor) {
      localStorageColor = state.projectMetadata.userBackgroundColor;
    }
    if (!localStorageColor) {
      // default estada color
      localStorageColor = "var(--app-background)"; // no itx
      //localStorageColor = "#e57e04";
    }
    return localStorageColor; // no itx
  }

  set uiBackgroundColor(newValue: string | undefined) {
    if (newValue) {
      localStorage.setItem("ui-background-color", newValue); // no itx
      state.projectMetadata.userBackgroundColor = newValue;
      // property name for setProperty must not include the var(...) wrapper
      document.documentElement.style.setProperty("--app-background", newValue); // no itx
    } else {
      localStorage.removeItem("ui-background-color"); // no itx
      state.projectMetadata.userBackgroundColor = undefined;
      document.documentElement.style.removeProperty("--app-background"); // no itx
    }
  }

  get uiTextColor(): string {
    let localStorageColor = localStorage.getItem("ui-text-color") || undefined;
    if (!localStorageColor) {
      localStorageColor = state.projectMetadata.userTextColor;
    }
    if (!localStorageColor) {
      // default estada color
      localStorageColor = "white";
    }
    return localStorageColor; // no itx
  }

  set uiTextColor(newValue: string | undefined) {
    if (newValue) {
      localStorage.setItem("ui-text-color", newValue); // no itx
      state.projectMetadata.userTextColor = newValue;
      document.documentElement.style.setProperty("--text-color", newValue); // no itx
    } else {
      localStorage.removeItem("ui-text-color"); // no itx
      state.projectMetadata.userTextColor = undefined;
      document.documentElement.style.removeProperty("--text-color"); // no itx
    }
  }

  ///////////////////////////////////////////////////////////////////////////////////////

  /// Lists 1,0526315789473684210526315789474
  showAddNewObj = false;

  isBrowserLanguageEn(): boolean {
    return navigator.language == "en-US" || navigator.language == "en";
  }

  drawingItemSize = 10;

  connectionGrabSize = "10px";
  diagnosticMode = false;
  etsIsOnline = false;

  get companyName(): string {
    const name = this.companyNameBack?.trim();
    return !name || name.toLowerCase() === "estada" ? "ESTADA" : this.companyNameBack!;
  }

  private companyUrlBack?: string;

  get companyUrl(): string {
    return this.companyUrlBack || "estada.de";
  }

  set companyUrl(value: string) {
    if (this.companyUrlBack === value) return;
    this.beforeChange("companyUrlBack");
    this.companyUrlBack = value;
    this.changed("companyUrlBack");
  }
  set companyName(companyName: string) {
    if (this.companyNameBack === companyName) return;
    this.beforeChange("companyNameBack");
    this.companyNameBack = companyName;
    this.changed("companyNameBack");
    // change in backend?
  }

  favoriteSelectedProjects = this.getFavoriteSelectedProjects();
  getFavoriteSelectedProjects(): Array<string> {
    const favoriteSelectedProjectsString = localStorage.getItem("favoriteSelectedProjects");
    if (favoriteSelectedProjectsString) {
      const res = JSON.parse(localStorage.getItem("favoriteSelectedProjects") || "");
      return res;
    }
    return [];
  }

  setFavoriteSelectedProjects(newValue: Array<string>) {
    localStorage.setItem("favoriteSelectedProjects", JSON.stringify(newValue));
    const user = localStorage.getItem("user");
    if (user && user != "") {
      const userDetails = JSON.parse(user);
      userDetails["favourited_projects"] = JSON.stringify(newValue); // no itx
      localStorage.setItem("user", JSON.stringify(userDetails));
    }
  }

  knxGroupAddressGeneneration: "A" | "B" | "C" = "B";

  topologyAreaNames: Record<string, string> = {};
  topologyLineNames: Record<string, string> = {};

  getTopologyAreaName(areaNumber: string | number): string | undefined {
    return this.topologyAreaNames[String(areaNumber)];
  }

  setTopologyAreaName(areaNumber: string | number, name: string): void {
    const key = String(areaNumber);
    const storedName = name.trim() ? name : undefined;
    if (this.topologyAreaNames[key] === storedName) return;

    const nextNames = { ...this.topologyAreaNames };
    if (storedName !== undefined) {
      nextNames[key] = storedName;
    } else {
      delete nextNames[key];
    }

    this.beforeChange("topologyAreaNames");
    this.topologyAreaNames = nextNames;
    this.changed("topologyAreaNames");
  }

  getTopologyLineName(areaNumber: string | number, lineNumber: string | number): string | undefined {
    return this.topologyLineNames[`${areaNumber}.${lineNumber}`];
  }

  setTopologyLineName(areaNumber: string | number, lineNumber: string | number, name: string): void {
    const key = `${areaNumber}.${lineNumber}`;
    const storedName = name.trim() ? name : undefined;
    if (this.topologyLineNames[key] === storedName) return;

    const nextNames = { ...this.topologyLineNames };
    if (storedName !== undefined) {
      nextNames[key] = storedName;
    } else {
      delete nextNames[key];
    }

    this.beforeChange("topologyLineNames");
    this.topologyLineNames = nextNames;
    this.changed("topologyLineNames");
  }

  moveTopologyLineName(areaNumber: string | number, fromLine: string | number, toLine: string | number): void {
    const fromKey = `${areaNumber}.${fromLine}`;
    const name = this.topologyLineNames[fromKey];
    if (name === undefined) return;

    const nextNames = { ...this.topologyLineNames };
    delete nextNames[fromKey];
    nextNames[`${areaNumber}.${toLine}`] = name;
    this.beforeChange("topologyLineNames");
    this.topologyLineNames = nextNames;
    this.changed("topologyLineNames");
  }

  moveTopologyAreaLineNames(fromArea: string | number, toArea: string | number): void {
    const fromPrefix = `${fromArea}.`;
    const entries = Object.entries(this.topologyLineNames).filter(([key]) => key.startsWith(fromPrefix));
    const areaName = this.topologyAreaNames[String(fromArea)];
    if (!entries.length && areaName === undefined) return;

    const nextNames = { ...this.topologyLineNames };
    const nextAreaNames = { ...this.topologyAreaNames };
    for (const [key, name] of entries) {
      delete nextNames[key];
      nextNames[`${toArea}.${key.slice(fromPrefix.length)}`] = name;
    }
    if (areaName !== undefined) {
      delete nextAreaNames[String(fromArea)];
      nextAreaNames[String(toArea)] = areaName;
    }
    this.beforeChange("topologyLineNames");
    this.beforeChange("topologyAreaNames");
    this.topologyLineNames = nextNames;
    this.topologyAreaNames = nextAreaNames;
    this.changed("topologyLineNames");
    this.changed("topologyAreaNames");
  }

  clearTopologyAreaNames(areaNumber: string | number): void {
    const key = String(areaNumber);
    const linePrefix = `${areaNumber}.`;
    const nextLineNames = { ...this.topologyLineNames };
    for (const nameKey of Object.keys(nextLineNames)) {
      if (nameKey.startsWith(linePrefix)) delete nextLineNames[nameKey];
    }
    const nextAreaNames = { ...this.topologyAreaNames };
    delete nextAreaNames[key];
    if (
      Object.keys(nextLineNames).length === Object.keys(this.topologyLineNames).length &&
      Object.keys(nextAreaNames).length === Object.keys(this.topologyAreaNames).length
    ) return;

    this.beforeChange("topologyLineNames");
    this.beforeChange("topologyAreaNames");
    this.topologyLineNames = nextLineNames;
    this.topologyAreaNames = nextAreaNames;
    this.changed("topologyLineNames");
    this.changed("topologyAreaNames");
  }

  getPersistentProperties(): string[] {
    return [
      "topologyAreaNames",
      "topologyLineNames",
      "companyNameBack", // no itx
      "companyUrlBack",
      "userNameBack", // no itx
      "consumerScale", // no itx
      "masterPdfConfig",
      "floorsHaveIndividualAxes",
      "c1TagByIndex",
      "c1TagEditable",
      "connectionDashboardVisibleTrees",
      "consumerScaleFloorPlan", // no itx
      "colorTheme", // no itx
      "connectionGrabSize", // no itx
      "showPageBorder", // no itx
      "connectionWidth", // no itx
      "showConsumerNames", // no itx
      "companyLogo", // no itx
      "devicePrefix",
      "fuseTagSchema",
      "includeElcbTagInFuseFullTag",
      "showConnectionTexts", // no itx
      "consumerPräfix", // no itx
      "favoriteSelectedProjects", // no itx
      "knxGroupAddressGeneneration", // no itx
      "prefixMode", // no itx
      "defaultBackgroundPicture", // no itx

      // dummy, just to keep "save" working
      // ... are stored in localstorage

      "storageMode", // no itx
      "uiMode", // no itx
      "showMenuButtonTooltips", // no itx
      "useNavigatorBreakLines", // no itx
      "openWithOneClick", // no itx

      "groupAddresses", // no itx

      "showIcons", // no itx
      "showTags", // no itx

      // "drawingItemColors", nicht benutzer änderbatr, gem diskussion 2.9.2025

      "deleteNotUsedSignalTypes", // no itx

      ...super.getPersistentProperties(),
    ];
  }
  /** Migrates the old dashboard setting while saving only the current property name. */
  override fromPersistentData(data: any): any {
    // UI language belongs to the user, not to a project or an imported settings record.
    if (data) {
      const { language, uiLanguage, autoAdjustEloDisplayPosition: _removedEloDisplayFlag, ...projectSettings } = data;
      data = { ...projectSettings, floorsHaveIndividualAxes: projectSettings.floorsHaveIndividualAxes === true };
    }
    if (
      data &&
      data.connectionDashboardVisibleTrees === undefined &&
      Object.prototype.hasOwnProperty.call(data, "multiTreeVisibleTrees")
    ) {
      data = { ...data, connectionDashboardVisibleTrees: data.multiTreeVisibleTrees };
    }
    if (data) {
      const { elcbAndFuseSettings, ...settings } = data;
      if (settings.fuseTagSchema === undefined && elcbAndFuseSettings !== undefined) {
        settings.fuseTagSchema = elcbAndFuseSettings === "F" ? "B" : "A";
      }
      if (settings.includeElcbTagInFuseFullTag === undefined) {
        settings.includeElcbTagInFuseFullTag = settings.fuseTagSchema === "B";
      }
      if (settings.devicePrefix) {
        const devicePrefix = { ...settings.devicePrefix };
        delete devicePrefix.fiLs;
        settings.devicePrefix = {
          ...this.devicePrefix,
          ...devicePrefix,
        };
      }
      data = settings;
    }
    return super.fromPersistentData(data);
  }

  async loadFromEstadaStorage(
    data: any | undefined,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => any,
  ): Promise<boolean> {
    return super.loadFromEstadaStorage(data, parent, loadData);
  }

  // get Username in short from local Storage
  // getUserShort(): string {
  //   var userName2: string = localStorage.getItem("user")
  //     ? (localStorage.getItem("user") as string)
  //     : "";
  //   if (this.userShort) {
  //     return this.userShort;
  //   } else {
  //     const unserName = JSON.parse(userName2);
  //     return unserName.name;
  //   }
  // }

  _prefixesDIN: any = {
    Button: "-",
    Actor: "-",
    Floor: "=",
    Room: ".",
    Page: ".",
    Cabinet: "+",
    Channel: ".",
    ButtonChannel: ":",
    Signal: ":",
    Consumer: ".",
    Scene: "",
    ELCB: "-",
    Fuse: "-",
    Terminal: ":",
    TerminalStrip: "-",
  };

  _prefixesDOT: any = {
    Button: ".",
    Actor: ".",
    Floor: "",
    Room: ".",
    Page: ".",
    Cabinet: "",
    Channel: ".",
    ButtonChannel: ".",
    Signal: ":",
    Consumer: ".",
    Scene: "",
    ELCB: ".",
    Fuse: ".",
    Terminal: ".",
    TerminalStrip: ".",
  };

  getTagPrefixByClass(className: string): string {
    if (this.prefixMode == PrefixMode.DIN) {
      return this._prefixesDIN[className] || "";
    } else {
      return this._prefixesDOT[className] || "";
    }
  }

  prefixMode = PrefixMode.DIN;

  debugTranslation = false;

  deleteNotUsedSignalTypes = true;

  self = this;

  //////////////////////////////////////////////////////////////////////////
  // group address name generation default values
  default_buttonChannelNameTemplate =
    "${signalType}" +
    " - " +
    itx("components.ButtonKey", "Taste") +
    ": " +
    "${buttonChannel.simpleFullTag} - ${buttonChannel.nameOrConnectedNames}"; // no itx

  default_consumerNameTemplate = "${consumer.fullTag} ${consumer.fullname} - ${signalType}"; // no itx
  default_multiplexerNameTemplate = "${multiplexer.tag} - ${multiplexer.name} - ${signalType}"; // no itx
  //////////////////////////////////////////////////////////////////////////

  groupAddresses = {
    useLockForSwitch: true,
    useLockForDim: true,
    useLockForJalousie: true,

    buttonChannelNameTemplate: this.default_buttonChannelNameTemplate,
    consumerNameTemplate: this.default_consumerNameTemplate,
    multiplexerNameTemplate: this.default_multiplexerNameTemplate,
  };

  applyGroupAddressNameTemplates() {
    this.groupAddressNameGenerator.setTemplates();
  }
}
